import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core'
import { TitleStrategy, provideRouter, withInMemoryScrolling } from '@angular/router'
import { provideHttpClient, withInterceptors } from '@angular/common/http'
import {
  provideAnimationsAsync,
} from '@angular/platform-browser/animations/async'
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core'
import { provideHighcharts } from 'highcharts-angular'
import { HotToastService, provideHotToastConfig } from '@ngxpert/hot-toast'

import { routes } from './app.routes'
import { authInterceptor } from './shared/interceptors/auth.interceptor'
import { CyprusDateAdapter } from './shared/adapters/cyprus-date-adapter'
import { PageTitleStrategy } from './shared/services/page-title.strategy'
import { provideScrollRestoration } from './shared/utils/scroll-restoration'
import { provideOverlayHistory } from './shared/services/overlay-history.service'
import { provideSeasonInUrl } from './shared/services/season.service'
import { DedupedHotToastService } from './shared/services/deduped-hot-toast.service'
import { provideFullscreenLayers } from './shared/services/fullscreen-layers'

// Phones show fewer toasts at once: each one is a full-width bar there.
const TOAST_IS_PHONE = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches

// Cyprus date format configuration
export const CYPRUS_DATE_FORMATS = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    // 'disabled' keeps the router recording positions and emitting Scroll events;
    // provideScrollRestoration() decides what to do with them (see that file for why).
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'disabled' })),
    provideScrollRestoration(),
    provideOverlayHistory(),
    // (Release 7, 04/11/2026) Dialogs and toasts opened over a player in full screen show inside it
    // instead of behind it -- see that file for why the CDK's own version was not enough.
    provideFullscreenLayers(),
    provideSeasonInUrl(),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideHttpClient(
      withInterceptors([authInterceptor]),
    ),
    provideAnimationsAsync(),
    provideHighcharts({
      modules: () => [
        import('highcharts/esm/highcharts-more'),
        import('highcharts/esm/modules/variable-pie'),
        // Goal Analysis graphs (phase 2, 02/11/2026): the start-to-finish flow.
        import('highcharts/esm/modules/sankey'),
      ],
    }),
    // Toasts. Bottom-right keeps them off the header and the row menus on tables; on phones
    // styles.scss turns them into a full-width bar above the home indicator. Look and feel
    // live in styles.scss (.scios-toast), so the per-type className is what styles them.
    // Success clears fast, errors stay long enough to read.
    provideHotToastConfig({
      duration: 4000,
      position: 'bottom-right',
      visibleToasts: TOAST_IS_PHONE ? 2 : 3,
      dismissible: true,
      role: 'status',
      ariaLive: 'polite',
      theme: 'toast',
      success: { content: '', duration: 3000, className: 'scios-toast scios-toast--success' },
      error: { content: '', duration: 8000, className: 'scios-toast scios-toast--error' },
      warning: { content: '', duration: 6000, className: 'scios-toast scios-toast--warning' },
      info: { content: '', duration: 4000, className: 'scios-toast scios-toast--info' },
      loading: { content: '', className: 'scios-toast scios-toast--info' },
      blank: { content: '', className: 'scios-toast' },
    }),
    { provide: HotToastService, useClass: DedupedHotToastService },
    // Cyprus date format configuration
    { provide: MAT_DATE_LOCALE, useValue: 'en-CY' },
    { provide: MAT_DATE_FORMATS, useValue: CYPRUS_DATE_FORMATS },
    { provide: DateAdapter, useClass: CyprusDateAdapter },
  ],
}
